<!-- 我的预约 -->
<template>
    <div class="page">
        <!-- 顶部用户信息 -->
        <div class="header">
            <div class="user-row">
                <div class="avatar-wrap">
                    <div class="avatar">
                        <img :src="defaultAvatar" alt="头像" />
                    </div>
                    <span class="avatar-check">
                        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="8" fill="#22C55E"/>
                            <path d="M4.5 8.2l2.2 2.2L11.5 5.5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                </div>
                <div class="phone-entry">
                    <span class="phone">{{ maskedPhone }}</span>
                </div>
                <button
                    class="logout-header-btn"
                    type="button"
                    @click="handleLogout"
                >
                    退出登录
                </button>
            </div>

            <!-- 状态筛选 -->
            <div class="status-tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    type="button"
                    class="status-tab"
                    :class="{ active: activeTab === tab.key }"
                    @click="onTabClick(tab.key)"
                >
                    <span class="tab-label">{{ tab.label }}</span>
                    <span class="tab-count">{{ tab.count }}</span>
                </button>
            </div>
        </div>

        <!-- 列表 / 空状态 -->
        <div class="content">
            <div v-if="filteredList.length" class="booking-list">
                <div
                    v-for="item in filteredList"
                    :key="item.id"
                    class="booking-card"
                    :class="cardClass(item)"
                >
                    <div class="card-top">
                        <div class="badge-group">
                            <!-- 进行中 -->
                            <template v-if="item.status === 'ongoing'">
                                <span class="badge badge-available">
                                    <i class="dot"></i>进行中
                                </span>
                                <span
                                    v-if="item.hasDoorAccess"
                                    class="badge badge-access"
                                >门禁已生效</span>
                            </template>
                            <!-- 待使用 -->
                            <template v-else-if="item.status === 'ready'">
                                <span class="badge badge-ready">待使用</span>
                                <span class="badge badge-tip">开始前5分钟可开门</span>
                            </template>
                            <!-- 已结束 -->
                            <template v-else-if="item.status === 'finished'">
                                <span class="badge badge-finished">已结束</span>
                                <span
                                    v-if="item.cancelled"
                                    class="badge badge-cancelled"
                                >已取消</span>
                            </template>
                            <!-- 已拒绝 -->
                            <template v-else-if="item.status === 'rejected'">
                                <span class="badge badge-rejected">已拒绝</span>
                            </template>
                            <!-- 待审核等其他状态 -->
                            <template v-else>
                                <span class="badge badge-status">{{ statusLabel(item.status) }}</span>
                                <span
                                    v-if="!item.hasDoorAccess"
                                    class="badge badge-warn"
                                >未获得开门权限</span>
                            </template>
                        </div>
                        <button
                            v-if="item.status === 'pending' || item.status === 'ongoing'"
                            class="link-btn"
                            type="button"
                            @click="contactManager(item)"
                        >联系项目管家</button>
                    </div>

                    <div class="card-main">
                        <div class="card-info">
                            <div class="booking-time">{{ item.date }} · {{ item.timeRange }}</div>
                            <div class="booking-title">
                                {{ item.roomName }}<template v-if="item.floor">({{ item.floor }})</template>
                            </div>
                            <div class="booking-meta">
                                {{ item.buildingName || '—' }}
                            </div>
                            <div class="booking-meta booking-meta-attendees">
                                受邀人数：{{ item.attendeeCount }}人(受邀人可开门锁)
                            </div>
                        </div>
                        <div class="card-actions">
                            <button
                                class="action-btn"
                                type="button"
                                @click="viewDetail(item)"
                            >查看详情</button>
                            <button
                                v-if="item.status === 'ongoing'"
                                class="action-btn action-btn-primary"
                                type="button"
                                @click="openDoor(item)"
                            >
                                <svg class="unlock-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 11V8a5 5 0 019.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.8"/>
                                    <circle cx="12" cy="16" r="1.4" fill="currentColor"/>
                                </svg>
                                开门
                            </button>
                            <button
                                v-if="canShowCancelBooking(item)"
                                class="action-btn"
                                type="button"
                                @click="cancelBooking(item)"
                            >取消预约</button>
                            <button
                                v-if="item.status === 'finished'"
                                class="action-btn"
                                type="button"
                                @click="rebook(item)"
                            >再次预约</button>
                        </div>
                    </div>

                    <div
                        v-if="item.status === 'rejected' && item.reason"
                        class="reject-reason-bar"
                    >
                        拒绝原因：{{ item.reason }}
                    </div>
                </div>
            </div>

            <div v-else class="empty">
                <div class="empty-icon">
                    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="8" y="12" width="32" height="28" rx="4" stroke="#A8B4C4" stroke-width="2.2"/>
                        <path d="M8 20h32" stroke="#A8B4C4" stroke-width="2.2"/>
                        <path d="M16 8v6M32 8v6" stroke="#A8B4C4" stroke-width="2.2" stroke-linecap="round"/>
                        <path d="M18 28h4M26 28h4M18 34h12" stroke="#A8B4C4" stroke-width="2.2" stroke-linecap="round"/>
                    </svg>
                </div>
                <p class="empty-title">暂无相关预约</p>
                <p class="empty-desc">预约会议室后，记录会自动出现在这里</p>
            </div>
        </div>

        <confirm-unlock
            v-model="showUnlock"
            :reservation-id="unlockTarget.id"
            :room-name="unlockTarget.roomName"
            :floor="unlockTarget.floor"
        />
    </div>
</template>

<script>
import confirmUnlock from './confirmUnlock.vue'
import scrollKeep from '@/tools/scrollKeep'
import { notifyUserInfoUpdated, isUnauthorizedError } from '@/tools/utils'
import { getReservationListRequest, cancelReservationRequest } from '@/request/api'
import { Toast } from 'vant'
import defaultAvatar from '@/assets/icon_avatar.png'

const ACTIVE_TAB_KEY = 'myBooking_activeTab'
const TAB_KEYS = ['pending', 'approved']
const LEGACY_TAB_KEYS = ['ongoing', 'ready', 'finished']
const FIVE_MIN_MS = 5 * 60 * 1000

function pad(n) {
    return n < 10 ? `0${n}` : `${n}`
}

function parseApiDateTime(str) {
    const raw = String(str || '').trim()
    if (!raw) return null
    const d = new Date(raw.replace(/-/g, '/'))
    return Number.isNaN(d.getTime()) ? null : d
}

function formatDisplayDate(date) {
    return `${date.getMonth() + 1}/${date.getDate()}`
}

function formatTimeFromDate(date) {
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function parseAttendeesList(attendees) {
    if (attendees == null || attendees === '') return []
    if (Array.isArray(attendees)) {
        return attendees.map((v) => String(v || '').trim()).filter(Boolean)
    }
    return String(attendees)
        .split(/[,，]/)
        .map((s) => s.trim())
        .filter(Boolean)
}

function getApiReservationStatus(item) {
    const raw = item.status != null ? item.status : item.reservationStatus
    const n = Number(raw)
    return Number.isNaN(n) ? null : n
}

/**
 * 已通过预约：结束时间 < 当前时间-5分钟 → 已结束；
 * 当前时间 < 开始时间-5分钟 → 待使用；其余 → 进行中
 */
function resolveApprovedUsageStatus(startDate, endDate, now = Date.now()) {
    if (!startDate || !endDate) {
        return 'ready'
    }
    const startMs = startDate.getTime()
    const endMs = endDate.getTime()
    const nowMinus5 = now - FIVE_MIN_MS
    const startMinus5 = startMs - FIVE_MIN_MS

    if (endMs < nowMinus5) {
        return 'finished'
    }
    if (now < startMinus5) {
        return 'ready'
    }
    return 'ongoing'
}

function resolveDisplayTabStatus(item, startDate, endDate, now = Date.now()) {
    const apiStatus = getApiReservationStatus(item)

    if (apiStatus === 1) {
        return 'pending'
    }

    // 接口 status：5-已拒绝
    if (apiStatus === 5) {
        return 'rejected'
    }

    return resolveApprovedUsageStatus(startDate, endDate, now)
}

/** 顶部 Tab：待审核 / 已通过 */
function resolveFilterTab(item) {
    const apiStatus = getApiReservationStatus(item)
    if (apiStatus === 1) {
        return 'pending'
    }
    return 'approved'
}

function mapReservationItem(item, userInfo) {
    if (!item || typeof item !== 'object') return null
    const id = item.id != null ? item.id : item.reservationId
    if (id == null) return null

    const startRaw = item.startTime || item.startDateTime || item.beginTime
    const endRaw = item.endTime || item.endDateTime || item.finishTime

    let date = item.date || ''
    let timeRange = item.timeRange || item.time || ''
    const startDate = parseApiDateTime(startRaw)
    const endDate = parseApiDateTime(endRaw)
    const filterTab = resolveFilterTab(item)
    const status = resolveDisplayTabStatus(item, startDate, endDate)
    if (startDate && endDate) {
        date = formatDisplayDate(startDate)
        timeRange = `${formatTimeFromDate(startDate)}—${formatTimeFromDate(endDate)}`
    }

    const room = item.room && typeof item.room === 'object' ? item.room : {}
    const roomName = String(room.name || item.roomName || '').trim()
    const floor = room.floor != null && String(room.floor).trim() !== ''
        ? String(room.floor).trim()
        : ''
    const buildingName = String(room.buildingName || item.buildingName || '').trim()
    const location = buildingName || '—'

    const attendeeList = parseAttendeesList(item.attendees)
    const attendeeCount = attendeeList.length

    const cancelled = !!(
        item.cancelled
        || item.canceled
        || item.isCancel
        || item.isCancelled
        || Number(item.cancelStatus) === 1
    )

    let hasDoorAccess = false
    let doorAccessFromApi = false
    if (item.hasDoorAccess != null) {
        doorAccessFromApi = true
        hasDoorAccess = item.hasDoorAccess === true || item.hasDoorAccess === 1 || item.hasDoorAccess === '1'
    } else if (item.doorAccess != null) {
        doorAccessFromApi = true
        hasDoorAccess = item.doorAccess === true || item.doorAccess === 1 || item.doorAccess === '1'
    } else {
        hasDoorAccess = status === 'ongoing'
    }

    return {
        id,
        roomId: room.id != null ? room.id : (item.roomId != null ? item.roomId : item.room_id),
        filterTab,
        status,
        startTimeMs: startDate ? startDate.getTime() : null,
        endTimeMs: endDate ? endDate.getTime() : null,
        doorAccessFromApi,
        hasDoorAccess,
        cancelled,
        bookingNo: item.bookingNo || item.reservationNo || item.orderNo || item.code || '',
        date,
        timeRange,
        roomName,
        floor,
        buildingName,
        location,
        attendeeCount,
        adminPhone: String(room.adminPhone || item.adminPhone || '').trim(),
        contactName: item.contactName || item.userName || userInfo.userName || '',
        contactPhone: item.contactPhone || item.phone || userInfo.phone || '',
        rentCompanyName: String(item.rentCompanyName || '').trim(),
        rentCompanyHouseNo: String(item.rentCompanyHouseNo || '').trim(),
        title: String(item.title || '').trim(),
        payAmount: item.payAmount != null
            ? Number(item.payAmount)
            : (item.amount != null ? Number(item.amount) : 0),
        statusRemark: item.statusRemark || item.remark || (cancelled ? '用户取消' : ''),
        reason: String(item.reason || item.rejectReason || '').trim(),
        attendees: attendeeList,
        relation: String(item.relation || '').trim(),
    }
}

function readSavedActiveTab() {
    const saved = sessionStorage.getItem(ACTIVE_TAB_KEY)
    if (TAB_KEYS.includes(saved)) return saved
    if (LEGACY_TAB_KEYS.includes(saved)) return 'approved'
    return 'pending'
}

export default {
    name: 'myBooking',
    mixins: [scrollKeep('myBooking')],
    components: {
        confirmUnlock,
    },
    data() {
        return {
            defaultAvatar,
            phone: '',
            activeTab: readSavedActiveTab(),
            tabs: [
                { key: 'pending', label: '待审核', count: 0 },
                { key: 'approved', label: '已审核', count: 0 },
            ],
            bookingList: [],
            listLoading: false,
            showUnlock: false,
            unlockTarget: {
                id: '',
                roomName: '',
                floor: '',
            },
        }
    },
    computed: {
        maskedPhone() {
            const phone = this.phone || ''
            if (/^1\d{10}$/.test(phone)) {
                return phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2')
            }
            return phone || '未绑定手机号'
        },
        filteredList() {
            const now = Date.now()
            return this.bookingList
                .filter((item) => item.filterTab === this.activeTab)
                .map((item) => this.withLiveApprovedStatus(item, now))
        },
    },
    watch: {
        activeTab(val) {
            sessionStorage.setItem(ACTIVE_TAB_KEY, val)
        },
    },
    activated() {
        const saved = readSavedActiveTab()
        if (saved !== this.activeTab) {
            this.activeTab = saved
        }
        this.loadUser()
        this.loadBookings()
    },
    methods: {
        onTabClick(key) {
            this.activeTab = key
            sessionStorage.setItem(ACTIVE_TAB_KEY, key)
        },
        withLiveApprovedStatus(item, now = Date.now()) {
            if (!item || item.filterTab !== 'approved') return item
            if (item.status === 'rejected') return item
            if (item.startTimeMs == null || item.endTimeMs == null) return item

            const status = resolveApprovedUsageStatus(
                new Date(item.startTimeMs),
                new Date(item.endTimeMs),
                now,
            )
            const hasDoorAccess = item.doorAccessFromApi
                ? item.hasDoorAccess
                : status === 'ongoing'

            if (item.status === status && item.hasDoorAccess === hasDoorAccess) {
                return item
            }

            return {
                ...item,
                status,
                hasDoorAccess,
            }
        },
        canShowCancelBooking(item) {
            if (!item || item.filterTab !== 'pending') return false
            return item.relation === '创建者'
        },
        loadUser() {
            try {
                const info = JSON.parse(localStorage.getItem('userInfo') || '{}')
                this.phone = info.phone || ''
            } catch (e) {
                this.phone = ''
            }
        },
        extractResponseList(res) {
            if (Array.isArray(res)) return res
            if (res && Array.isArray(res.data)) return res.data
            if (res && res.data && Array.isArray(res.data.list)) return res.data.list
            if (res && Array.isArray(res.list)) return res.list
            return []
        },
        loadBookings() {
            if (this.listLoading) return

            let userInfo = {}
            try {
                userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
            } catch (e) {
                userInfo = {}
            }

            this.listLoading = true
            Toast.loading({
                message: '正在加载',
                forbidClick: true,
                duration: 0,
            })

            getReservationListRequest({})
                .then((res) => {
                    this.bookingList = this.extractResponseList(res)
                        .map((item) => mapReservationItem(item, userInfo))
                        .filter(Boolean)
                    this.refreshTabCount()
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    this.bookingList = []
                    this.refreshTabCount()
                    const msg = (err && (err.message || err.msg)) || '获取预约列表失败'
                    this.$toast && this.$toast(msg)
                })
                .finally(() => {
                    this.listLoading = false
                    Toast.clear()
                })
        },
        refreshTabCount() {
            this.tabs = this.tabs.map((tab) => ({
                ...tab,
                count: this.bookingList.filter((item) => item.filterTab === tab.key).length,
            }))
        },
        cardClass(item) {
            return {
                'card-ongoing': item.status === 'ongoing',
                'card-ready': item.status === 'ready',
                'card-finished': item.status === 'finished',
                'card-rejected': item.status === 'rejected',
            }
        },
        statusLabel(status) {
            const map = {
                pending: '待审核',
                ongoing: '进行中',
                ready: '待使用',
                finished: '已结束',
                rejected: '已拒绝',
            }
            return map[status] || ''
        },
        handleLogout() {
            this.$dialog
                .confirm({
                    title: '退出登录',
                    message: '确认退出当前账号吗？',
                })
                .then(() => {
                    localStorage.removeItem('userInfo')
                    localStorage.removeItem('pendingReviewCount')
                    notifyUserInfoUpdated()
                    this.$router.replace('/home')
                })
                .catch(() => {})
        },
        contactManager(item) {
            const phone = String((item && item.adminPhone) || '').trim()
            if (!phone) {
                this.$toast && this.$toast('暂无项目管家联系电话')
                return
            }
            window.location.href = `tel:${phone}`
        },
        viewDetail(item) {
            sessionStorage.setItem('bookingDetail', JSON.stringify(item))
            this.$router.push({
                path: '/myBooking/bookingDetails',
                query: {
                    id: item.id,
                    status: item.status,
                },
            })
        },
        openDoor(item) {
            if (!item || item.status !== 'ongoing') return
            this.unlockTarget = {
                id: item.id,
                roomName: item.roomName || '',
                floor: item.floor || '',
            }
            this.showUnlock = true
        },
        rebook(item) {
            if (!item) return
            const nameToId = {
                共享会议室一: 1,
                共享会议室二: 2,
                共享会议室三: 3,
            }
            const id = item.roomId || nameToId[item.roomName] || 3
            this.$router.push({
                path: '/home/selectMeetingRoomTimeSlot',
                query: {
                    id,
                    name: item.roomName,
                    location: item.buildingName || item.location,
                },
            })
        },
        cancelBooking(item) {
            if (!item || item.id == null) return
            const roomLabel = item.floor
                ? `${item.roomName}(${item.floor})`
                : (item.roomName || '该会议室')
            this.$dialog
                .confirm({
                    title: '取消预约',
                    message: `确认取消「${roomLabel}」的预约吗？`,
                })
                .then(() => {
                    Toast.loading({
                        message: '取消中...',
                        forbidClick: true,
                        duration: 0,
                    })
                    return cancelReservationRequest({ reservationId: item.id })
                        .then(() => {
                            this.bookingList = this.bookingList.filter((row) => row.id !== item.id)
                            this.refreshTabCount()
                            this.$toast && this.$toast('已取消预约')
                        })
                        .catch((err) => {
                            if (isUnauthorizedError(err)) return
                            const msg = (err && (err.message || err.msg)) || '取消预约失败'
                            this.$toast && this.$toast(msg)
                        })
                        .finally(() => {
                            Toast.clear()
                        })
                })
                .catch(() => {})
        },
    },
}
</script>

<style scoped>
.page {
    min-height: calc(100vh - 1.2rem - env(safe-area-inset-bottom));
    background: #f2f4f8;
    /* 列表底部少量间距；tabBar 高度由 MainTab placeholder 预留 */
    padding-bottom: 0.2rem;
    box-sizing: border-box;
}

.header {
    background: #0d2c54;
    padding: 0.36rem 0.32rem 0.28rem;
    box-sizing: border-box;
}

.user-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.36rem;
}

.avatar-wrap {
    position: relative;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
}

.avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    background: #f5e6d3;
}

.avatar img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
}

.avatar-check {
    position: absolute;
    right: -0.02rem;
    bottom: -0.02rem;
    width: 0.32rem;
    height: 0.32rem;
}

.avatar-check svg {
    width: 100%;
    height: 100%;
    display: block;
}

.phone-entry {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    margin-left: 0.2rem;
    padding: 0;
    color: #ffffff;
    text-align: left;
}

.phone {
    font-size: 0.34rem;
    font-weight: 600;
    color: #ffffff;
    letter-spacing: 0.01rem;
}

.logout-header-btn {
    flex-shrink: 0;
    align-self: center;
    margin-left: 0.16rem;
    height: 0.56rem;
    padding: 0 0.22rem;
    border: 0.02rem solid rgba(255, 255, 255, 0.45);
    border-radius: 0.28rem;
    background: transparent;
    color: #ffffff;
    font-size: 0.24rem;
    font-weight: 500;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
}

.logout-header-btn:active {
    background: rgba(255, 255, 255, 0.12);
}

.status-tabs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.12rem;
}

.status-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 0.64rem;
    border: none;
    border-radius: 0.16rem;
    background: transparent;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.24rem;
    padding: 0 0.08rem;
    cursor: pointer;
    white-space: nowrap;
}

.status-tab.active {
    background: rgba(255, 255, 255, 0.18);
    color: #ffffff;
}

.tab-label {
    margin-right: 0.08rem;
}

.tab-count {
    min-width: 0.32rem;
    height: 0.32rem;
    padding: 0 0.08rem;
    border-radius: 0.16rem;
    background: #2b7de9;
    color: #ffffff;
    font-size: 0.2rem;
    line-height: 0.32rem;
    text-align: center;
    box-sizing: border-box;
}

.content {
    min-height: 6rem;
    background: #f2f4f8;
}

.booking-list {
    padding: 0.24rem 0.28rem 0.32rem;
}

.booking-card {
    background: #ffffff;
    border: 1px solid #f0d9b0;
    border-radius: 0.24rem;
    padding: 0.28rem 0.28rem 0.24rem;
    margin-bottom: 0.2rem;
    box-sizing: border-box;
}

.booking-card.card-ongoing {
    border-color: #b8e8d0;
    box-shadow: 0 0 0 0.04rem rgba(72, 199, 142, 0.12);
}

.booking-card.card-ready {
    border-color: #d6e6fb;
    box-shadow: 0 0.08rem 0.24rem rgba(43, 125, 233, 0.06);
}

.booking-card.card-finished {
    border-color: #e6ebf2;
    box-shadow: 0 0.08rem 0.24rem rgba(30, 58, 95, 0.04);
}

.booking-card.card-rejected {
    border-color: #f5d0d0;
    box-shadow: 0 0.08rem 0.24rem rgba(220, 80, 80, 0.06);
}

.reject-reason-bar {
    margin-top: 0.16rem;
    padding: 0.16rem 0.2rem;
    border-radius: 0.12rem;
    background: #fff5f5;
    color: #c0392b;
    font-size: 0.22rem;
    line-height: 1.45;
    word-break: break-all;
}

.booking-card.card-finished .booking-time {
    color: #334155;
}

.booking-card.card-finished .booking-title {
    color: #334155;
}

.card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 0.16rem;
}

.badge-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.1rem;
    min-width: 0;
}

.badge {
    display: inline-flex;
    align-items: center;
    padding: 0.02rem 0.12rem;
    border-radius: 0.06rem;
    font-size: 0.2rem;
    line-height: 1.5;
    white-space: nowrap;
}

.badge-status {
    background: #fff4e5;
    color: #d4923a;
}

.badge-warn {
    background: transparent;
    border: 1px solid #e8b56a;
    color: #d4923a;
}

.badge-available {
    background: transparent;
    color: #1f9d63;
    padding-left: 0;
    font-weight: 500;
}

.badge-available .dot {
    width: 0.12rem;
    height: 0.12rem;
    margin-right: 0.08rem;
    border-radius: 50%;
    background: #2fc97d;
    flex-shrink: 0;
}

.badge-access {
    background: #e8f9f0;
    color: #1f9d63;
    border-radius: 0.2rem;
    font-weight: 500;
}

.badge-ready {
    background: #e8f1ff;
    color: #2b7de9;
    border-radius: 0.08rem;
    font-weight: 500;
}

.badge-tip {
    background: #eef5ff;
    color: #4b8fe8;
    border-radius: 0.2rem;
    font-weight: 500;
}

.badge-finished {
    background: #eef1f6;
    color: #8a97a8;
    border-radius: 0.08rem;
    font-weight: 500;
}

.badge-cancelled {
    background: #eef1f6;
    color: #8a97a8;
    border-radius: 0.08rem;
    font-weight: 500;
}

.badge-rejected {
    background: #fdeaea;
    color: #d64545;
    border-radius: 0.08rem;
    font-weight: 500;
}

.card-main {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
}

.card-info {
    flex: 1;
    min-width: 0;
    padding-right: 0.2rem;
}

.booking-time {
    font-size: 0.28rem;
    font-weight: 700;
    color: #2b7de9;
    line-height: 1.35;
}

.booking-title {
    margin-top: 0.08rem;
    font-size: 0.34rem;
    font-weight: 700;
    color: #1e3a5f;
    line-height: 1.3;
}

.booking-meta {
    margin-top: 0.08rem;
    font-size: 0.22rem;
    color: #8a9bb0;
    line-height: 1.4;
}

.booking-meta-attendees {
    margin-top: 0.04rem;
}

.link-btn {
    flex-shrink: 0;
    margin-left: 0.16rem;
    border: none;
    background: transparent;
    padding: 0;
    color: #2b7de9;
    font-size: 0.24rem;
    line-height: 1.5;
    white-space: nowrap;
    cursor: pointer;
}

.card-actions {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.14rem;
    flex-shrink: 0;
}

.action-btn {
    min-width: 1.56rem;
    height: 0.6rem;
    padding: 0 0.24rem;
    border: 1px solid #c5d3e8;
    border-radius: 0.3rem;
    background: #ffffff;
    color: #334155;
    font-size: 0.24rem;
    white-space: nowrap;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.action-btn:active {
    background: #f5f8fc;
}

.action-btn-primary {
    border-color: #2b7de9;
    background: #2b7de9;
    color: #ffffff;
    font-weight: 500;
}

.action-btn-primary:active {
    background: #236dce;
}

.unlock-icon {
    width: 0.28rem;
    height: 0.28rem;
    margin-right: 0.08rem;
    flex-shrink: 0;
}

.empty {
    padding: 1.8rem 0.4rem 0;
    text-align: center;
}

.empty-icon {
    width: 0.72rem;
    height: 0.72rem;
    margin: 0 auto 0.28rem;
    opacity: 0.85;
}

.empty-icon svg {
    width: 100%;
    height: 100%;
    display: block;
}

.empty-title {
    margin: 0;
    font-size: 0.32rem;
    font-weight: 600;
    color: #1e3a5f;
}

.empty-desc {
    margin: 0.16rem 0 0;
    font-size: 0.24rem;
    color: #9aa8bc;
}
</style>
